/**
 * surveys.service.ts
 *
 * Typed API call wrappers for the Surveys domain.
 * All authenticated methods use the shared apiClient (Axios instance with JWT interceptor).
 * submitResponse uses a plain axios call with no auth header.
 * Returns unwrapped data from the response envelope — callers never see Axios wrappers.
 */
import { apiClient } from './apiClient';
import type { PaginatedResponse, ApiResponse } from '@/types/api.types';

// ─── Domain types (mirrors actual DB schema) ─────────────────────────────────

export interface SurveyQuestionOption {
  id: number;
  optionText: string;
  sOrder: number;
  preselect: boolean;
}

export interface SurveyQuestionType {
  id: number;
  typeTitle: string;
}

export interface Survey {
  id: number;
  accountId: number;
  title: string;
  surveyType: number | null;
  contactMe: boolean | null;
  headerMessage: string | null;
  submissionMessage: string | null;
  pageTitle: string | null;
  isActive: boolean;
  expiresInDays: number | null;
  shortcode: string | null;
  color1: string | null;
  color2: string | null;
  fontColor: string | null;
  surveyOrder: number | null;
  leadSourceId: number | null;
  submissionUrl: string | null;
  displayLogo: boolean | null;
  emailContentId: number | null;
  smsId: number | null;
  autoresponderID: number | null;
  dateCreated: string | null;
  questionCount?: number;
  responseCount?: number;
  directLink?: string | null;
  legacyDirectLink?: string | null;
}

export interface SurveyDetail extends Survey {
  questions: SurveyQuestion[];
  logicRules: SurveyLogicRule[];
}

export interface SurveyQuestion {
  id: number;                          // surveysOrderID
  surveyId: number;
  accountId: number;
  surveysItemId: number;
  questionContent: string;
  questionType: number;                // integer, references question_types table
  questionTypeTitle: string;
  instructionText: string | null;
  customQuestionType: number;
  surveysQuestionTypeCustomId: number;
  recordFieldText: string | null;
  recordFieldId: number;
  isSummary: boolean;
  isRequired: boolean;
  addOther: boolean;
  checkAll: boolean;
  order: number;                       // iOrder
  options: SurveyQuestionOption[];     // from surveys_options table
}

export interface SurveyLogicRuleDetail {
  id: number;
  surveysOrderId: number;
  targetItemId: number;
  logicBoolean: number;
  surveysOptionId: number;
  responseContent: string;
}

export interface SurveyLogicRule {
  id: number;                          // surveysItemLogicID
  surveyId: number;
  accountId: number;
  surveysOrderId: number;
  logicAction: number;                 // integer
  logicCondition: number;              // integer
  rules: SurveyLogicRuleDetail[];     // nested detail rules
}

export interface SurveyAssignment {
  id: number;
  surveyId: number;
  accountId: number;
  patientId: number;
  token: string;
  sentAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  patientName?: string;
  surveyTitle?: string;
}

export interface SurveyResponse {
  id: number;
  assignmentId: number;
  surveyId: number;
  accountId: number;
  submittedAt: string;
  answers: SurveyAnswer[];
}

export interface SurveyAnswer {
  questionId: number;
  questionLabel: string;
  questionType: number;
  value: string | null;
}

export interface QuestionRatingSummary {
  questionId: number;
  questionLabel: string;
  questionType: number;
  average: number | null;
  count: number;
  distribution: Record<string, number>;
  npsScore?: number;
  promoters?: number;
  passives?: number;
  detractors?: number;
}

export interface RatingsReport {
  surveyId: number;
  dateFrom: string | null;
  dateTo: string | null;
  questions: QuestionRatingSummary[];
}

// ─── Request payload types ────────────────────────────────────────────────────

export interface SurveyListParams {
  search?: string;
  isActive?: boolean;
  /** Multi-value status checklist (`'active' | 'inactive'`). */
  statuses?: string[];
  statusesNegate?: boolean;
  facilityId?: number;
  facilityIds?: number[];
  facilityIdsNegate?: boolean;
  page?: number;
  perPage?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface CreateSurveyPayload {
  title: string;
  headerMessage?: string;
  isActive?: boolean;
  expiresInDays?: number;
}

export interface UpdateSurveyPayload {
  title?: string;
  pageTitle?: string | null;
  headerMessage?: string | null;
  submissionMessage?: string | null;
  submissionUrl?: string | null;
  surveyType?: number | null;
  displayLogo?: boolean | null;
  color1?: string | null;
  color2?: string | null;
  fontColor?: string | null;
  shortcode?: string | null;
  expiresInDays?: number | null;
  isActive?: boolean;
}

export interface CreateQuestionPayload {
  questionContent: string;
  questionType: number;
  isRequired?: boolean;
  addOther?: boolean;
  checkAll?: boolean;
  instructionText?: string;
  order?: number;
  surveysItemId?: number;
}

export interface UpdateQuestionPayload {
  questionContent?: string;
  questionType?: number;
  isRequired?: boolean;
  addOther?: boolean;
  checkAll?: boolean;
  isSummary?: boolean;
  instructionText?: string;
  recordFieldText?: string | null;
  recordFieldId?: number | null;
  order?: number;
  options?: Array<{ id?: number; optionText: string; sOrder: number; preselect: boolean }>;
}

export interface ReorderQuestionsPayload {
  orderedIds: number[];
}

export interface AddLogicRulePayload {
  surveysOrderId: number;
  logicAction: number;
  logicCondition?: number;
}

export interface AssignSurveyPayload {
  patientIds: number[];
  scheduledAt?: string;
}

export interface AssignSurveyResult {
  assignments: SurveyAssignment[];
  count: number;
}

export interface SubmitResponsePayload {
  answers: Array<{ questionId: number; value: string | null }>;
}

export interface AssignmentListParams {
  page?: number;
  perPage?: number;
  surveyId?: number;
}

export interface ResponseListParams {
  page?: number;
  perPage?: number;
}

export interface RatingsReportParams {
  dateFrom?: string;
  dateTo?: string;
}

// ─── Survey CRUD ──────────────────────────────────────────────────────────────

/**
 * Fetch a paginated list of surveys with optional filters.
 */
export function listSurveys(
  params: SurveyListParams = {},
): Promise<PaginatedResponse<Survey>> {
  return apiClient
    .get<PaginatedResponse<Survey>>('/surveys', { params })
    .then((r) => r.data);
}

/**
 * Fetch a single survey with its questions and logic rules.
 */
export function getSurvey(id: number): Promise<SurveyDetail> {
  return apiClient
    .get<ApiResponse<SurveyDetail>>(`/surveys/${id}`)
    .then((r) => r.data.data);
}

/**
 * Create a new survey.
 */
export function createSurvey(data: CreateSurveyPayload): Promise<Survey> {
  return apiClient
    .post<ApiResponse<Survey>>('/surveys', data)
    .then((r) => r.data.data);
}

/**
 * Update an existing survey's metadata.
 */
export function updateSurvey(id: number, data: UpdateSurveyPayload): Promise<Survey> {
  return apiClient
    .put<ApiResponse<Survey>>(`/surveys/${id}`, data)
    .then((r) => r.data.data);
}

/**
 * Soft-delete a survey.
 */
export function deleteSurvey(id: number): Promise<void> {
  return apiClient.delete(`/surveys/${id}`).then(() => undefined);
}

/**
 * Clone a survey (duplicates questions + logic rules).
 */
export function cloneSurvey(id: number): Promise<Survey> {
  return apiClient
    .post<ApiResponse<Survey>>(`/surveys/${id}/clone`)
    .then((r) => r.data.data);
}

// ─── Question types ───────────────────────────────────────────────────────────

/**
 * Fetch the list of supported question types from the database.
 */
export function getQuestionTypes(): Promise<SurveyQuestionType[]> {
  return apiClient
    .get<ApiResponse<SurveyQuestionType[]>>('/surveys/question-types')
    .then((r) => r.data.data);
}

// ─── Questions ────────────────────────────────────────────────────────────────

/**
 * Fetch all questions for a survey, ordered by sortOrder.
 */
export function getQuestions(surveyId: number): Promise<SurveyQuestion[]> {
  return apiClient
    .get<ApiResponse<SurveyQuestion[]>>(`/surveys/${surveyId}/questions`)
    .then((r) => r.data.data);
}

/**
 * Add a question to a survey.
 */
export function addQuestion(
  surveyId: number,
  data: CreateQuestionPayload,
): Promise<SurveyQuestion> {
  return apiClient
    .post<ApiResponse<SurveyQuestion>>(`/surveys/${surveyId}/questions`, data)
    .then((r) => r.data.data);
}

/**
 * Update a question.
 */
export function updateQuestion(
  surveyId: number,
  questionId: number,
  data: UpdateQuestionPayload,
): Promise<SurveyQuestion> {
  return apiClient
    .put<ApiResponse<SurveyQuestion>>(`/surveys/${surveyId}/questions/${questionId}`, data)
    .then((r) => r.data.data);
}

/**
 * Delete a question (also removes any logic rules that reference it).
 */
export function deleteQuestion(surveyId: number, questionId: number): Promise<void> {
  return apiClient
    .delete(`/surveys/${surveyId}/questions/${questionId}`)
    .then(() => undefined);
}

/**
 * Reorder questions by providing the full ordered array of IDs.
 */
export function reorderQuestions(
  surveyId: number,
  data: ReorderQuestionsPayload,
): Promise<SurveyQuestion[]> {
  return apiClient
    .post<ApiResponse<SurveyQuestion[]>>(`/surveys/${surveyId}/questions/reorder`, data)
    .then((r) => r.data.data);
}

// ─── Logic Rules ──────────────────────────────────────────────────────────────

/**
 * Fetch all logic rules for a survey.
 */
export function getLogicRules(surveyId: number): Promise<SurveyLogicRule[]> {
  return apiClient
    .get<ApiResponse<SurveyLogicRule[]>>(`/surveys/${surveyId}/logic`)
    .then((r) => r.data.data);
}

/**
 * Add a conditional logic rule to a survey.
 */
export function addLogicRule(
  surveyId: number,
  data: AddLogicRulePayload,
): Promise<SurveyLogicRule> {
  return apiClient
    .post<ApiResponse<SurveyLogicRule>>(`/surveys/${surveyId}/logic`, data)
    .then((r) => r.data.data);
}

/**
 * Delete a logic rule.
 */
export function deleteLogicRule(surveyId: number, ruleId: number): Promise<void> {
  return apiClient
    .delete(`/surveys/${surveyId}/logic/${ruleId}`)
    .then(() => undefined);
}

// ─── Assignments ──────────────────────────────────────────────────────────────

/**
 * List all survey assignments for the account.
 */
export function listAssignments(
  params: AssignmentListParams = {},
): Promise<PaginatedResponse<SurveyAssignment>> {
  return apiClient
    .get<PaginatedResponse<SurveyAssignment>>('/surveys/assignments', { params })
    .then((r) => r.data);
}

/**
 * Assign a survey to one or more patients.
 * Generates a unique token per patient saved to DB.
 */
export function assignToPatient(
  surveyId: number,
  data: AssignSurveyPayload,
): Promise<AssignSurveyResult> {
  return apiClient
    .post<ApiResponse<AssignSurveyResult>>(`/surveys/${surveyId}/assign`, data)
    .then((r) => r.data.data);
}

// ─── Response submission (PUBLIC) ─────────────────────────────────────────────

/**
 * Submit a survey response using the patient's token.
 * This endpoint is public — no authentication required.
 */
export function submitResponse(
  token: string,
  data: SubmitResponsePayload,
): Promise<{ responseId: number }> {
  return apiClient
    .post<ApiResponse<{ responseId: number }>>(`/surveys/respond/${token}`, data)
    .then((r) => r.data.data);
}

// ─── Responses ────────────────────────────────────────────────────────────────

/**
 * Fetch paginated survey responses for a specific survey.
 */
export function getResponses(
  surveyId: number,
  params: ResponseListParams = {},
): Promise<PaginatedResponse<SurveyResponse>> {
  return apiClient
    .get<PaginatedResponse<SurveyResponse>>(`/surveys/${surveyId}/responses`, { params })
    .then((r) => r.data);
}

// ─── Reports ──────────────────────────────────────────────────────────────────

/**
 * Fetch the ratings/NPS report for a survey with an optional date range.
 * NPS breakdown: 0-6 detractors, 7-8 passives, 9-10 promoters.
 */
export function getRatingsReport(
  surveyId: number,
  params: RatingsReportParams = {},
): Promise<RatingsReport> {
  return apiClient
    .get<ApiResponse<RatingsReport>>(`/surveys/${surveyId}/reports/ratings`, { params })
    .then((r) => r.data.data);
}

export interface SurveyListStats {
  total: number;
  active: number;
}

export function getSurveyStats(params: SurveyListParams = {}): Promise<SurveyListStats> {
  return apiClient
    .get<{ data: SurveyListStats }>('/surveys/stats', { params })
    .then((r) => r.data.data);
}
